import React from 'react';
import { motion } from 'framer-motion';
import { Award, Wrench, Headphones, Zap, ShoppingCart, Package, Play } from 'lucide-react';

const Features: React.FC = () => {
  const features = [
    {
      icon: Award,
      title: 'Kuratierte Auswahl',
      description: 'Jedes Produkt wird von uns getestet und für Streaming optimiert. Keine Kompromisse bei der Qualität.',
      color: 'text-primary-500'
    },
    {
      icon: Wrench,
      title: 'Komplettes Setup',
      description: 'Von der Bestellung bis zum ersten Stream - wir bieten Remote- und Vor-Ort-Service an wenn benötigt. Bitte Kontakt per WhatsApp aufnehmen.',
      color: 'text-secondary-500'
    },
    {
      icon: Headphones,
      title: 'Creator-First Support',
      description: 'Unser Team besteht aus aktiven Streamern. Wir verstehen deine Bedürfnisse und helfen gerne.',
      color: 'text-accent-500'
    }
  ];

  return (
    <section id="features" className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Warum tech4.stream?
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Wir kuratieren die besten Streaming-Setups, damit du dich auf deinen Content konzentrieren kannst
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              className="card text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
            >
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-dark-800 mb-6 ${feature.color}`}>
                <feature.icon size={32} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">
                {feature.title}
              </h3>
              <p className="text-gray-300">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Steps Section */}
        <motion.div 
          className="mt-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Dein Weg zum perfekten Stream
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <motion.div 
              className="card text-center relative overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-500 to-secondary-500"></div>
              <div className="relative z-10">
                <div className="w-20 h-20 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-6 shadow-lg">
                  <ShoppingCart size={32} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  Equipment wählen
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Finde das perfekte Bundle für deine Streaming-Ziele
                </p>
              </div>
            </motion.div>
            
            {/* Step 2 */}
            <motion.div 
              className="card text-center relative overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary-500 to-accent-500"></div>
              <div className="relative z-10">
                <div className="w-20 h-20 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-6 shadow-lg">
                  <Package size={32} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  Erhalte deine Bestellung und baue auf
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Wir helfen dir gerne bei der Einrichtung
                </p>
              </div>
            </motion.div>
            
            {/* Step 3 */}
            <motion.div 
              className="card text-center relative overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-accent-500 to-primary-500"></div>
              <div className="relative z-10">
                <div className="w-20 h-20 bg-gradient-to-r from-accent-500 to-primary-500 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-6 shadow-lg">
                  <Play size={32} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  Live gehen
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Starte sofort mit professioneller Qualität
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
